import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { getUsers, createUser, updateUser, getOrgTree } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import { CAPABILITIES, defaultsForRole } from '../permissions';
import { usePageSearchState } from '../lib/pageSearch';
import { DASHBOARD_OPTIONS, defaultDashboardFor, dashboardLabel } from '../lib/dashboards';

/* ═══════════════════════════════════════════════════════════════════════════
   Team Management — laid out like Facility Navigator's Users page (Duncan,
   2026-10-08): a "Team" header with Add user, one tile per role in use, then
   List / Org chart / Permissions. The list is alphabetical, one line a person,
   with Edit and Deactivate first; people are deactivated (history kept), never
   deleted from here. The header and tiles stay put while the list scrolls: the
   page is sized to the window and the list scrolls inside its card, because
   position:sticky can't hold anything here (.main-content's overflow-x makes it
   the sticky container, and it never scrolls — the window does).
   Full-width: .app-main supplies the 24px gutter; AppHeader supplies the title.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── icons (inline SVG, no glyphs) ─────────────── */
const TmIcon = ({ name, size = 16, sw = 1.6 }) => {
  const p = (d) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>{d}</svg>
  );
  switch (name) {
    case 'plus':      return p(<path d="M12 5v14M5 12h14" />);
    case 'x':         return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'check':     return p(<path d="m5 12 5 5L20 7" />);
    case 'edit':      return p(<><path d="M4 20h4L19 9l-4-4L4 16Z" /><path d="m13.5 6.5 4 4" /></>);
    case 'chevron-d': return p(<path d="m6 9 6 6 6-6" />);
    case 'pin':       return p(<><path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" /><circle cx="12" cy="10" r="2.6" /></>);
    case 'user-plus': return p(<><circle cx="9" cy="8" r="3.3" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M19 8v6M22 11h-6" /></>);
    case 'list':      return p(<><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></>);
    case 'sitemap':   return p(<><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" /><path d="M12 8v4M6 16v-2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2" /></>);
    case 'alert-tri': return p(<><path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /><path d="M12 9v4" /><circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" /></>);
    case 'corner':    return p(<path d="M7 4v8a4 4 0 0 0 4 4h6" />);
    case 'shield':    return p(<><path d="M12 3 5 6v5c0 4.4 3 7.4 7 9 4-1.6 7-4.6 7-9V6Z" /><path d="m9.5 12 1.8 1.8L15 10" /></>);
    case 'user-off':  return p(<><circle cx="9" cy="8" r="3.3" /><path d="M3.5 20a5.5 5.5 0 0 1 9.6-3.7" /><path d="m16 15 5 5M21 15l-5 5" /></>);
    case 'search':    return p(<><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.2-4.2" /></>);
    case 'spin':      return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={sw} strokeLinecap="round" style={{ display: 'block', animation: 'tmSpin .8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-6.2-8.55" />
      </svg>
    );
    default: return null;
  }
};

/* ─────────────── role-tone map (the categorical core) ─────────────── */
const ROLES = {
  admin:                 { value: 'admin',                 label: 'Admin',                 short: 'Admin',    tone: 'var(--type-inspection)', text: 'var(--type-inspection)', hint: 'Full system access & settings' },
  vp:                    { value: 'vp',                    label: 'Vice President',        short: 'VP',       tone: 'var(--warning)',         text: 'var(--warning)',         hint: 'Oversees all operations' },
  construction_director: { value: 'construction_director', label: 'Construction Director', short: 'Director', tone: 'var(--type-labor)',      text: 'var(--type-labor)',      hint: 'Leads the construction division' },
  construction_manager:  { value: 'construction_manager',  label: 'Construction Manager',  short: 'Manager',  tone: 'var(--type-material)',   text: 'var(--type-material)',   hint: 'Manages project teams' },
  pm:                    { value: 'pm',                    label: 'Project Manager',       short: 'PM',       tone: 'var(--success)',         text: 'var(--success)',         hint: 'Runs individual projects' },
  e3_direct_director:    { value: 'e3_direct_director',    label: 'E3 Direct Director',        short: 'E3 Dir',   tone: 'var(--type-labor)',      text: 'var(--type-labor)',      hint: 'Leads the E3 Direct division' },
  e3_direct_pm:          { value: 'e3_direct_pm',          label: 'E3 Direct Project Manager', short: 'E3 PM',    tone: 'var(--success)',         text: 'var(--success)',         hint: 'Runs E3 Direct projects' },
  sales_manager:         { value: 'sales_manager',         label: 'Sales Manager',             short: 'Sales Mgr', tone: 'var(--warning)',         text: 'var(--warning)',         hint: 'Leads the sales team' },
  sales_person:          { value: 'sales_person',          label: 'Sales Person',              short: 'Sales',    tone: 'var(--accent)',          text: 'var(--accent-text)',     hint: 'Individual sales rep' },
  project_developer:     { value: 'project_developer',     label: 'Project Developer',         short: 'Dev',      tone: 'var(--text-3)',          text: 'var(--text-2)',          hint: 'Develops projects (separate dashboard)', neutral: true },
  accounting:            { value: 'accounting',            label: 'Accounting',                short: 'Acct',     tone: 'var(--type-procurement)', text: 'var(--type-procurement)', hint: 'Billing, AR and every job (Accounting dashboard)' },
};
const role = (k) => ROLES[k] || { value: k, label: k, short: k, tone: 'var(--text-3)', text: 'var(--text-2)', hint: '', neutral: true };
const roleSoft = (r) => r.neutral ? 'var(--bg-surface-2)' : `color-mix(in oklch, ${r.tone} 14%, var(--bg-surface))`;
const roleRing = (r) => r.neutral ? 'var(--border-subtle)' : `color-mix(in oklch, ${r.tone} 22%, transparent)`;

const ROLE_OPTIONS = ['admin', 'vp', 'construction_director', 'construction_manager', 'pm', 'e3_direct_director', 'e3_direct_pm', 'sales_manager', 'sales_person', 'project_developer', 'accounting'].map((v) => ({ value: v, label: ROLES[v].label }));
// Display/org ordering (lower number = higher rank). Admin and VP share the same
// PERMISSION tier, but here VP gets its own rank (1) just below Admin so it sorts
// as its own group instead of interleaving alphabetically inside the admins.
// E3 Direct tiers sit alongside their Construction equivalents (director=2, PM=4);
// Sales Manager alongside Manager (3), Sales Person below PM (5).
const ROLE_RANK = { admin: 0, vp: 1, construction_director: 2, construction_manager: 3, pm: 4, e3_direct_director: 2, e3_direct_pm: 4, sales_manager: 3, sales_person: 5, project_developer: 6, accounting: 5 };

// A user may report to anyone of strictly higher rank (lower ROLE_RANK number).
// e.g. a PM can report to a Manager, Director, or VP; a VP to an Admin. Admin is
// the top of the org and reports to no one. Reporting is optional in all cases.
const RANK_ORDER = ['admin', 'vp', 'construction_director', 'e3_direct_director', 'construction_manager', 'sales_manager', 'pm', 'e3_direct_pm', 'sales_person', 'accounting', 'project_developer'];
const validManagerRoles = (r) => {
  const rank = ROLE_RANK[r];
  if (rank == null) return [];
  return RANK_ORDER.filter((k) => ROLE_RANK[k] < rank);
};

/* ─────────────── formatters ─────────────── */
// last_login arrives as Postgres text ("2026-10-06 13:47:40.619068+00") or ISO. Made
// strictly ISO (T, 3-digit fraction, "+00:00", or Z when there's no offset) so every
// browser parses it as UTC. null → null (shown as "—").
const parseTs = (v) => {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  let s = String(v).trim().replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  if (/[+-]\d\d$/.test(s)) s += ':00';
  else if (!/(Z|[+-]\d\d:?\d\d)$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d) ? null : d;
};
const fmtSignIn = (v) => {
  const d = parseTs(v);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
};
const initials = (a, b) => {
  const f = (a || '').trim(), l = (b || '').trim();
  if (!f && !l) return '—';
  return ((f[0] || '') + (l[0] || '')).toUpperCase();
};
const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.name || '—';

/* ─────────────── small primitives ─────────────── */
const Avatar = ({ ini, size = 30, roleKey = null }) => {
  const toned = roleKey != null;
  const r = toned ? role(roleKey) : null;
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28, flex: '0 0 auto',
      background: toned ? roleSoft(r) : 'var(--bg-surface-2)',
      color: toned ? r.text : 'var(--text-2)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.37, fontWeight: 600, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
      border: '1px solid ' + (toned ? roleRing(r) : 'var(--border-subtle)'),
    }}>{ini}</div>
  );
};

const RoleBadge = ({ roleKey, size = 'md' }) => {
  const r = role(roleKey);
  const h = size === 'sm' ? 20 : 22;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: h, padding: '0 9px', borderRadius: 6,
      background: roleSoft(r), border: '1px solid ' + roleRing(r),
      fontSize: size === 'sm' ? 11 : 11.5, fontWeight: 600, color: r.text, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: r.tone, flex: '0 0 auto' }} />
      {r.label}
    </span>
  );
};

const StatusBadge = ({ active }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px 0 8px', borderRadius: 6,
    background: active ? 'color-mix(in oklch, var(--success) 14%, var(--bg-surface))' : 'var(--bg-surface-2)',
    border: '1px solid ' + (active ? 'color-mix(in oklch, var(--success) 22%, transparent)' : 'var(--border-subtle)'),
    fontSize: 13, fontWeight: 600, color: active ? 'var(--success)' : 'var(--text-4)', whiteSpace: 'nowrap',
  }}>
    <span style={{ width: 6, height: 6, borderRadius: 999, flex: '0 0 auto', background: active ? 'var(--success)' : 'var(--text-4)' }} />
    {active ? 'Active' : 'Inactive'}
  </span>
);

/* Facility Navigator's tile: the count over the role, a bar in the role's colour. One per
   role that has active people; what each role can do lives in Permissions. */
// Nine roles share one row, so the longest name gets its short form on the tile.
const TILE_LABEL = { e3_direct_pm: 'E3 Direct PM' };
const RoleTile = ({ roleKey, count }) => {
  const r = role(roleKey);
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 8, padding: '10px 12px', borderRadius: 12, minWidth: 0,
      background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)',
    }}>
      <span style={{ width: 4, borderRadius: 2, background: r.tone, flex: '0 0 auto' }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 650, lineHeight: 1.15, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{count}</div>
        <div title={r.label} style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.3, textWrap: 'pretty' }}>{TILE_LABEL[roleKey] || r.label}</div>
      </div>
    </div>
  );
};

/* Pills, as in Facility Navigator: the top tier (Admin, and VP, which shares its
   permissions) filled; every other role outlined. */
const RolePill = ({ roleKey }) => {
  const solid = roleKey === 'admin' || roleKey === 'vp';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 999, whiteSpace: 'nowrap',
      fontSize: 12.5, fontWeight: 500, lineHeight: 1,
      color: solid ? '#fff' : 'var(--text-1)', background: solid ? 'var(--accent)' : 'transparent',
      border: '1px solid ' + (solid ? 'var(--accent)' : 'var(--border-strong)'),
    }}>{role(roleKey).label}</span>
  );
};
const CustomChip = () => (
  <span title="Has custom permission overrides" style={{
    display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 999, marginLeft: 6, whiteSpace: 'nowrap',
    fontSize: 11.5, fontWeight: 500, color: 'var(--accent-text)', border: '1px solid color-mix(in oklch, var(--accent) 45%, transparent)',
  }}>custom</span>
);
const YouChip = () => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 999, marginLeft: 4,
    fontSize: 11.5, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)',
  }}>you</span>
);
const StatusPill = ({ active }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 999, whiteSpace: 'nowrap',
    fontSize: 12.5, fontWeight: 500, lineHeight: 1,
    color: active ? 'var(--success)' : 'var(--text-3)',
    border: '1px solid ' + (active ? 'color-mix(in oklch, var(--success) 45%, transparent)' : 'var(--border-default)'),
  }}>{active ? 'Active' : 'Inactive'}</span>
);
const IconBtn = ({ title, onClick, disabled, danger, children }) => (
  <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled}
    className={'tm-ib' + (danger ? ' tm-ib-danger' : '')} style={{
      width: 30, height: 30, borderRadius: 8, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: 'transparent', border: '1px solid transparent',
      color: disabled ? 'var(--text-4)' : (danger ? 'var(--danger)' : 'var(--text-2)'),
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
    }}>{children}</button>
);
const SearchBox = ({ value, onChange }) => {
  const [f, setF] = useState(false);
  return (
    <div style={{ position: 'relative', width: 280, maxWidth: '100%' }}>
      <span style={{ position: 'absolute', left: 10, top: 0, height: 34, display: 'inline-flex', alignItems: 'center', color: 'var(--text-3)', pointerEvents: 'none' }}>
        <TmIcon name="search" size={15} sw={1.8} />
      </span>
      <input value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => setF(true)} onBlur={() => setF(false)}
        placeholder="Search by name, email or role" aria-label="Search people"
        style={{ ...fieldBase(f), height: 34, paddingLeft: 32, paddingRight: value ? 30 : 12, fontSize: 14 }} />
      {value && (
        <button type="button" onClick={() => onChange('')} aria-label="Clear search" className="tm-modal-x" style={{
          position: 'absolute', right: 5, top: 5, width: 24, height: 24, borderRadius: 6, padding: 0, border: 'none',
          background: 'transparent', color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}><TmIcon name="x" size={13} sw={2} /></button>
      )}
    </div>
  );
};

/* ─────────────── List view ─────────────── */
const TH = ({ children, align = 'left' }) => (
  <th style={{
    position: 'sticky', top: 0, zIndex: 1, textAlign: align, padding: '0 16px', height: 38, whiteSpace: 'nowrap',
    background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)',
    fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)',
  }}>{children}</th>
);
const TD = ({ children, align = 'left', muted = false }) => (
  <td style={{ padding: '12px 16px', textAlign: align, verticalAlign: 'middle', borderBottom: '1px solid var(--border-subtle)', opacity: muted ? 0.62 : 1 }}>{children}</td>
);
const EditBtn = ({ onClick }) => (
  <button type="button" className="tm-edit" onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 5, height: 28, padding: '0 11px', borderRadius: 7,
    fontSize: 13.5, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap',
    background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer',
  }}>
    <TmIcon name="edit" size={13} sw={1.7} />Edit
  </button>
);

/* List columns, as in Facility Navigator: Actions first (Edit is why the page is open,
   and at the far right it was the first thing to fall off a narrow window), headings in
   sentence case. Every th style is set here: App.css capitalises and pads bare <th>. */
const LTH = ({ children, width }) => (
  <th style={{
    position: 'sticky', top: 0, zIndex: 1, textAlign: 'left', padding: '0 12px', height: 40, whiteSpace: 'nowrap', width,
    background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)',
    fontSize: 13.5, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--text-1)',
  }}>{children}</th>
);
const LTD = ({ children, muted = false, secondary = false }) => (
  <td style={{
    padding: '7px 12px', verticalAlign: 'middle', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-subtle)',
    fontSize: 14, color: secondary ? 'var(--text-2)' : 'var(--text-1)', opacity: muted ? 0.55 : 1,
  }}>{children}</td>
);

const UserRow = ({ user, isSelf, onEdit, onDeactivate }) => {
  const inactive = !user.is_active;
  const signIn = fmtSignIn(user.last_login);
  const custom = user.permission_overrides && typeof user.permission_overrides === 'object' && Object.keys(user.permission_overrides).length > 0;
  return (
    <tr className="tm-row" style={{ background: 'var(--bg-surface)' }}>
      <LTD>
        <div style={{ display: 'flex', gap: 2 }}>
          <IconBtn title="Edit" onClick={() => onEdit(user)}><TmIcon name="edit" size={15} sw={1.7} /></IconBtn>
          <IconBtn danger title={isSelf ? "You can't deactivate yourself" : (inactive ? 'Already inactive' : 'Deactivate')}
            disabled={isSelf || inactive} onClick={() => onDeactivate(user)}>
            <TmIcon name="user-off" size={15} sw={1.7} />
          </IconBtn>
        </div>
      </LTD>
      <LTD muted={inactive}>{fullName(user)}{isSelf && <> <YouChip /></>}</LTD>
      <LTD muted={inactive} secondary>{user.email}</LTD>
      <LTD muted={inactive}><span style={{ display: 'inline-flex', alignItems: 'center' }}><RolePill roleKey={user.role} />{custom && <CustomChip />}</span></LTD>
      <LTD muted={inactive} secondary>{user.reports_to_name || '—'}</LTD>
      <LTD><StatusPill active={!inactive} /></LTD>
      <LTD muted={inactive} secondary>{signIn || '—'}</LTD>
    </tr>
  );
};

const matches = (u, q) => !q || [fullName(u), u.email, role(u.role).label, u.reports_to_name, u.region]
  .some((x) => (x || '').toLowerCase().includes(q));

const ListView = ({ users, currentUser, query, frozen, onEdit, onDeactivate, onNew }) => {
  if (!users || users.length === 0) {
    return (
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
        <StateScreen
          kind="empty"
          title="No team members yet"
          body="Add your first user to start building roles, the reporting structure, and access."
          primary={{ label: 'Add user', onClick: onNew, icon: 'plus' }}
        />
      </div>
    );
  }
  const q = query.trim().toLowerCase();
  const rows = users.filter((u) => matches(u, q)).sort((a, b) => fullName(a).localeCompare(fullName(b)));
  // The card is the scroller: frozen, it shrinks to the space under the tiles and its
  // header row sticks; unfrozen it just grows and scrolls sideways when narrow.
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)',
      overflow: 'auto', ...(frozen ? { flex: '0 1 auto', minHeight: 0 } : {}),
    }}>
      <table style={{ width: '100%', minWidth: 760, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
        <thead>
          <tr>
            <LTH width={84}>Actions</LTH><LTH>Name</LTH><LTH>Email</LTH><LTH>Role</LTH><LTH>Reports to</LTH><LTH>Status</LTH><LTH>Last sign-in</LTH>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => <UserRow key={u.id} user={u} isSelf={!!currentUser && currentUser.id === u.id} onEdit={onEdit} onDeactivate={onDeactivate} />)}
          {rows.length === 0 && (
            <tr><td colSpan={7} style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)', borderBottom: 'none' }}>
              No one matches “{query.trim()}”.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────── Org Chart ─────────────── */
const OrgNode = ({ node, onEdit }) => {
  const inactive = node.is_active === false;
  return (
    <div className="tm-orgnode" style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10,
      background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', opacity: inactive ? 0.62 : 1,
    }}>
      <Avatar ini={initials(...(node.name || '').split(' '))} size={34} roleKey={node.role} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>{node.name}</span>
          <RoleBadge roleKey={node.role} size="sm" />
          {inactive && <StatusBadge active={false} />}
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--text-3)' }}>
          <TmIcon name="pin" size={11} sw={1.7} />{node.region || '—'}
        </span>
      </div>
      <EditBtn onClick={() => onEdit(node.id)} />
    </div>
  );
};

const Branch = ({ node, childrenOf, onEdit }) => {
  const kids = childrenOf(node.id);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <OrgNode node={node} onEdit={onEdit} />
      {kids.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 17, paddingLeft: 18, borderLeft: '1.5px solid var(--border-default)' }}>
          {kids.map((k) => <Branch key={k.id} node={k} childrenOf={childrenOf} onEdit={onEdit} />)}
        </div>
      )}
    </div>
  );
};

const OrgEmpty = () => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    gap: 12, padding: '52px 24px', minHeight: 280,
    background: 'var(--bg-surface)', border: '1px dashed var(--border-strong)', borderRadius: 12,
  }}>
    <div aria-hidden="true" style={{
      width: 52, height: 52, borderRadius: 12, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', color: 'var(--text-3)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <TmIcon name="sitemap" size={24} sw={1.5} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxWidth: 380 }}>
      <span style={{ fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>No reporting structure yet</span>
      <span style={{ fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.5, textWrap: 'pretty' }}>Set “Reports To” on users to build the tree.</span>
    </div>
  </div>
);

const OrgChart = ({ orgTree, onEdit }) => {
  const nodes = orgTree || [];
  const rank = (n) => (ROLE_RANK[n.role] ?? 99);
  const byRankName = (a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '');
  const childrenOf = (id) => nodes.filter((n) => n.reports_to === id).sort(byRankName);
  const roots = nodes.filter((n) => n.reports_to == null).sort(byRankName);
  if (nodes.length === 0 || roots.length === 0) return <OrgEmpty />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '18px 20px', background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      {roots.map((r) => <Branch key={r.id} node={r} childrenOf={childrenOf} onEdit={onEdit} />)}
    </div>
  );
};

/* ─────────────── Modal ─────────────── */
const ModalShell = ({ title, subtitle, onClose, busy, children, footer, width = 560 }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center',
      background: 'oklch(0.2 0.008 100 / 0.34)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', padding: 24,
    }}>
      <div role="dialog" aria-modal="true" style={{
        width, maxWidth: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 14,
        boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)', overflow: 'hidden',
        fontFamily: 'var(--font-sans)', color: 'var(--text-1)', letterSpacing: '-0.005em',
        display: 'flex', flexDirection: 'column', maxHeight: '90%', minHeight: 0,
      }}>
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{title}</h2>
            {subtitle && <p style={{ margin: '5px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>{subtitle}</p>}
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="tm-modal-x" style={{
            width: 26, height: 26, borderRadius: 6, color: 'var(--text-3)', flex: '0 0 auto',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid transparent', cursor: busy ? 'default' : 'pointer',
          }}><TmIcon name="x" size={14} /></button>
        </header>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px' }}>{children}</div>
        {footer && (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>{footer}</footer>
        )}
      </div>
    </div>
  );
};

const Label = ({ children, required, hint }) => (
  <label style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '0.01em', marginBottom: 6 }}>
    <span>{children}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}</span>
    {hint && <span style={{ fontWeight: 500, color: 'var(--text-4)', letterSpacing: 0 }}>{hint}</span>}
  </label>
);

const fieldBase = (focused, mono = false) => ({
  width: '100%', height: 36, padding: '0 12px', borderRadius: 8,
  fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)', fontSize: 14.5,
  color: 'var(--text-1)', background: 'var(--bg-surface)', outline: 'none', boxSizing: 'border-box',
  border: '1px solid ' + (focused ? 'var(--accent)' : 'var(--border-default)'),
  boxShadow: focused ? '0 0 0 3px var(--accent-ring)' : 'none',
  letterSpacing: mono ? '-0.02em' : '-0.005em', transition: 'border-color 120ms, box-shadow 120ms',
});

const TextInput = ({ value, onChange, placeholder, type = 'text', mono = false, autoFocus = false }) => {
  const [f, setF] = useState(false);
  return (
    <input value={value} type={type} autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)} onFocus={() => setF(true)} onBlur={() => setF(false)}
      placeholder={placeholder} style={fieldBase(f, mono)} />
  );
};

const SelectInput = ({ value, onChange, options, placeholder = 'Select…', disabled = false }) => {
  const [f, setF] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        onFocus={() => setF(true)} onBlur={() => setF(false)}
        style={{
          ...fieldBase(f && !disabled), appearance: 'none', WebkitAppearance: 'none',
          cursor: disabled ? 'default' : 'pointer', paddingRight: 34,
          color: disabled ? 'var(--text-4)' : (value ? 'var(--text-1)' : 'var(--text-4)'),
          background: disabled ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
        }}>
        {placeholder != null && <option value="" disabled>{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ position: 'absolute', top: 0, right: 11, height: 36, display: 'inline-flex', alignItems: 'center', color: 'var(--text-3)', pointerEvents: 'none' }}>
        <TmIcon name="chevron-d" size={15} sw={1.8} />
      </span>
    </div>
  );
};

const DisabledField = ({ children, icon = 'corner' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px', borderRadius: 8, background: 'var(--bg-surface-2)', border: '1px dashed var(--border-strong)', fontSize: 14, color: 'var(--text-4)' }}>
    <span style={{ color: 'var(--text-4)', flex: '0 0 auto', display: 'inline-flex' }}><TmIcon name={icon} size={14} sw={1.7} /></span>
    <span style={{ textWrap: 'pretty' }}>{children}</span>
  </div>
);

const ErrorBanner = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 8, background: 'var(--danger-soft)', border: '1px solid color-mix(in oklch, var(--danger) 22%, transparent)' }}>
    <span style={{ color: 'var(--danger)', flex: '0 0 auto', display: 'inline-flex', marginTop: 1 }}><TmIcon name="alert-tri" size={15} sw={1.8} /></span>
    <span style={{ fontSize: 14, color: 'var(--danger)', fontWeight: 500, lineHeight: 1.45, textWrap: 'pretty' }}>{children}</span>
  </div>
);

/* ─────────────── permission helpers (matrix + per-user overrides) ─────────────── */
// CAPABILITIES grouped in catalog order, e.g. [['Administration', [...]], ...].
const CAP_GROUPS = (() => {
  const order = [];
  const byGroup = {};
  for (const c of CAPABILITIES) {
    if (!byGroup[c.group]) { byGroup[c.group] = []; order.push(c.group); }
    byGroup[c.group].push(c);
  }
  return order.map((g) => [g, byGroup[g]]);
})();

// A small On/Off dot used in the read-only role matrix.
const GrantDot = ({ on }) => (
  <span title={on ? 'Granted' : 'Not granted'} style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 6,
    background: on ? 'color-mix(in oklch, var(--success) 14%, var(--bg-surface))' : 'var(--bg-surface-2)',
    border: '1px solid ' + (on ? 'color-mix(in oklch, var(--success) 24%, transparent)' : 'var(--border-subtle)'),
    color: on ? 'var(--success)' : 'var(--text-4)',
  }}>
    {on ? <TmIcon name="check" size={12} sw={2.4} /> : <span style={{ width: 7, height: 1.5, borderRadius: 1, background: 'currentColor' }} />}
  </span>
);

// Tri-state per-capability override: Default (inherit role) · Grant · Revoke.
const OverrideControl = ({ state, onChange }) => {
  // state: 'default' | 'grant' | 'revoke'
  const opts = [
    { v: 'default', label: 'Default' },
    { v: 'grant', label: 'Grant', tone: 'var(--success)' },
    { v: 'revoke', label: 'Revoke', tone: 'var(--danger)' },
  ];
  return (
    <div style={{ display: 'inline-flex', padding: 2, gap: 2, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
      {opts.map((o) => {
        const active = state === o.v;
        return (
          <button key={o.v} type="button" onClick={() => onChange(o.v)} style={{
            height: 24, padding: '0 10px', borderRadius: 6, border: '1px solid ' + (active ? (o.tone ? `color-mix(in oklch, ${o.tone} 40%, transparent)` : 'var(--border-default)') : 'transparent'),
            background: active ? (o.tone ? `color-mix(in oklch, ${o.tone} 14%, var(--bg-surface))` : 'var(--bg-surface)') : 'transparent',
            color: active ? (o.tone || 'var(--text-1)') : 'var(--text-3)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            boxShadow: active ? 'var(--shadow-xs)' : 'none', whiteSpace: 'nowrap',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
};

// The per-user override editor shown inside the create/edit modal.
const PermissionOverridesField = ({ roleKey, overrides, onChange }) => {
  const defaults = defaultsForRole(roleKey || 'pm');
  const stateFor = (key) => (overrides[key] === true ? 'grant' : overrides[key] === false ? 'revoke' : 'default');
  const setState = (key, next) => {
    const copy = { ...overrides };
    if (next === 'default') delete copy[key];
    else copy[key] = next === 'grant';
    onChange(copy);
  };
  const changedCount = Object.keys(overrides).length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <Label hint={roleKey ? `· baseline from ${role(roleKey).label}` : '· select a role first'}>Permissions</Label>
        {changedCount > 0 && (
          <button type="button" onClick={() => onChange({})} style={{
            fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          }}>Reset {changedCount} override{changedCount === 1 ? '' : 's'}</button>
        )}
      </div>
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden' }}>
        {CAP_GROUPS.map(([group, caps], gi) => (
          <div key={group}>
            <div style={{ padding: '7px 12px', background: 'var(--bg-surface-2)', borderTop: gi ? '1px solid var(--border-subtle)' : 'none', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{group}</div>
            {caps.map((cap) => {
              const st = stateFor(cap.key);
              const eff = st === 'grant' ? true : st === 'revoke' ? false : defaults[cap.key];
              return (
                <div key={cap.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{cap.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: eff ? 'var(--success)' : 'var(--text-4)' }}>{eff ? 'Allowed' : 'Blocked'}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.35, marginTop: 1, textWrap: 'pretty' }}>
                      {cap.description}<span style={{ color: 'var(--text-4)' }}> · default {defaults[cap.key] ? 'on' : 'off'}</span>
                    </div>
                  </div>
                  <OverrideControl state={st} onChange={(v) => setState(cap.key, v)} />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// The on/off switch for an account (Facility Navigator's "Active" toggle).
const ActiveSwitch = ({ checked, onChange }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} style={{
    display: 'inline-flex', alignItems: 'center', gap: 10, height: 36, padding: 0, background: 'transparent', border: 'none',
    cursor: 'pointer', fontSize: 14.5, color: 'var(--text-1)', fontFamily: 'var(--font-sans)',
  }}>
    <span style={{ position: 'relative', width: 36, height: 20, borderRadius: 999, flex: '0 0 auto', background: checked ? 'var(--accent)' : 'var(--border-strong)', transition: 'background 120ms' }}>
      <span style={{ position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: 999, background: '#fff', boxShadow: 'var(--shadow-xs)', transition: 'left 120ms' }} />
    </span>
    {checked ? 'Active' : 'Deactivated — can’t sign in'}
  </button>
);

const UserFormModal = ({ mode, user, orgTree, onClose, onSubmit }) => {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => ({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    role: user?.role || '',
    reports_to: user?.reports_to != null ? String(user.reports_to) : '',
    region: user?.region || '',
    is_active: user ? (user.is_active ? 'active' : 'inactive') : 'active',
    permission_overrides: (user?.permission_overrides && typeof user.permission_overrides === 'object' && !Array.isArray(user.permission_overrides))
      ? { ...user.permission_overrides } : {},
    dashboard: user?.dashboard || 'default', // 'default' = their role's (saved as NULL)
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A manager the last role change had to remove (they no longer outrank the person),
  // named under Reports To so the change is never silent.
  const [clearedBoss, setClearedBoss] = useState(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // Changing role keeps the manager when they still outrank the new role; otherwise
  // Reports To is cleared and says so (it used to clear without a word).
  const setRole = (v) => {
    const boss = form.reports_to ? (orgTree || []).find((n) => String(n.id) === form.reports_to) : null;
    const keep = !!boss && v !== 'admin' && v !== 'vp' && validManagerRoles(v).includes(boss.role);
    setClearedBoss(boss && !keep ? boss.name : null);
    setForm((f) => ({ ...f, role: v, reports_to: keep ? f.reports_to : '' }));
  };
  const setBoss = (v) => { setClearedBoss(null); set('reports_to')(v); };

  const noBoss = form.role === 'admin' || form.role === 'vp'; // VP & Admin are the top tier
  const validRoles = validManagerRoles(form.role);
  const editingId = user?.id ?? null;
  const managerOptions = (orgTree || [])
    .filter((n) => validRoles.includes(n.role) && n.id !== editingId)
    .sort((a, b) => (ROLE_RANK[a.role] ?? 99) - (ROLE_RANK[b.role] ?? 99) || (a.name || '').localeCompare(b.name || ''))
    .map((n) => ({ value: String(n.id), label: n.name + ' · ' + role(n.role).short }));

  const submit = async () => {
    if (busy) return; // double-submit guard
    if (!form.first_name.trim() || !form.last_name.trim()) return setError('First and last name are required.');
    if (!form.email.trim()) return setError('Email is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError('Enter a valid email address.');
    if (!form.role) return setError('Select a role.');

    setError(null);
    setBusy(true);
    const payload = {
      first_name: form.first_name.trim(), last_name: form.last_name.trim(), email: form.email.trim(),
      role: form.role,
      reports_to: form.reports_to ? parseInt(form.reports_to, 10) : null,
      region: form.region.trim() || null,
      is_active: isEdit ? (form.is_active === 'active') : true,
      permission_overrides: form.permission_overrides || {},
      dashboard: form.dashboard && form.dashboard !== 'default' ? form.dashboard : null,
    };
    try {
      await onSubmit(payload);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  let reportsToField;
  if (noBoss) {
    reportsToField = <DisabledField>N/A — {form.role === 'vp' ? 'VP' : 'Admin'} is the top of the org</DisabledField>;
  } else if (!form.role) {
    reportsToField = <DisabledField icon="corner">Select a role first.</DisabledField>;
  } else if (managerOptions.length === 0) {
    reportsToField = <DisabledField icon="corner">No higher-ranked user yet — leave blank for now.</DisabledField>;
  } else {
    reportsToField = <SelectInput value={form.reports_to} onChange={setBoss} placeholder="Select manager…" options={managerOptions} />;
  }

  return (
    <ModalShell
      title={isEdit ? 'Edit user' : 'Add user'} width={560} onClose={onClose} busy={busy}
      subtitle={isEdit
        ? <>Update <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>{fullName(user)}</strong>’s role, reporting line, region, and access.</>
        : 'Add a team member — set their role, reporting line, and access. They sign in with Microsoft.'}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
          <button type="button" onClick={() => !busy && onClose()} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8,
            fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap',
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          }}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 15px', borderRadius: 8,
            fontSize: 14, fontWeight: 600, color: '#fff', background: 'var(--accent)',
            border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))',
            boxShadow: '0 1px 0 oklch(0.42 0.06 135), 0 1px 2px oklch(0.4 0.05 135 / 0.25)', whiteSpace: 'nowrap',
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.85 : 1,
          }}>
            {busy ? <TmIcon name="spin" size={13} sw={2.4} /> : <TmIcon name={isEdit ? 'check' : 'user-plus'} size={13} sw={2.2} />}
            {busy ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save changes' : 'Add user')}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label required>First Name</Label>
            <TextInput value={form.first_name} onChange={set('first_name')} placeholder="e.g. Alex" autoFocus={!isEdit} />
          </div>
          <div>
            <Label required>Last Name</Label>
            <TextInput value={form.last_name} onChange={set('last_name')} placeholder="e.g. Morgan" />
          </div>
        </div>

        <div>
          <Label required>Email</Label>
          <TextInput value={form.email} onChange={set('email')} placeholder="name@e3es.com" type="email" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label required>Role</Label>
            <SelectInput value={form.role} onChange={setRole} placeholder="Select role…" options={ROLE_OPTIONS} />
            {form.role && <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.4 }}>{role(form.role).hint}</div>}
          </div>
          <div>
            <Label>Reports To</Label>
            {reportsToField}
            {clearedBoss && (
              <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.4, textWrap: 'pretty' }}>
                {clearedBoss} can’t manage {/^[AEIOU]/i.test(role(form.role).label) ? 'an' : 'a'} {role(form.role).label}, so Reports To was cleared.{noBoss ? '' : ' Pick someone in a higher role.'}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label>Dashboard</Label>
            <SelectInput value={form.dashboard} onChange={set('dashboard')}
              options={[{ value: 'default', label: `Role default · ${dashboardLabel(defaultDashboardFor(form.role))}` }, ...DASHBOARD_OPTIONS]} />
            <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.4 }}>The dashboard they see. Their role's unless you pick one.</div>
          </div>
          <div />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label>Region</Label>
            <TextInput value={form.region} onChange={set('region')} placeholder="e.g. Southeast" />
          </div>
          {isEdit ? (
            <div>
              <Label>Status</Label>
              <ActiveSwitch checked={form.is_active === 'active'} onChange={(on) => set('is_active')(on ? 'active' : 'inactive')} />
            </div>
          ) : <div />}
        </div>

        <div style={{ height: 1, background: 'var(--border-subtle)', margin: '2px 0' }} />
        <PermissionOverridesField
          roleKey={form.role}
          overrides={form.permission_overrides}
          onChange={(next) => setForm((f) => ({ ...f, permission_overrides: next }))}
        />
      </div>
    </ModalShell>
  );
};

/* ─────────────── success banner ─────────────── */
const SuccessBanner = ({ children, onDismiss }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--success-soft)', border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)' }}>
    <span style={{
      width: 22, height: 22, borderRadius: 6, flex: '0 0 auto', color: 'var(--success)',
      background: 'color-mix(in oklch, var(--success) 16%, var(--bg-surface))', border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}><TmIcon name="check" size={13} sw={2.2} /></span>
    <span style={{ flex: 1, fontSize: 14, color: 'var(--success)', fontWeight: 600, letterSpacing: '-0.005em' }}>{children}</span>
    <button type="button" onClick={onDismiss} aria-label="Dismiss" className="tm-banner-x" style={{
      width: 22, height: 22, borderRadius: 6, flex: '0 0 auto', color: 'var(--success)', background: 'transparent', border: 'none',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    }}><TmIcon name="x" size={13} sw={2} /></button>
  </div>
);

/* ─────────────── Permissions matrix view ─────────────── */
// Read-only role × capability grid (the role DEFAULTS). Per-user grants/revokes
// live on each user (Edit → Permissions); this shows the baseline every role
// starts from. `users` lets us flag which users carry overrides.
const PermissionsMatrix = ({ users }) => {
  const roleKeys = ['admin', 'vp', 'construction_director', 'construction_manager', 'pm', 'e3_direct_director', 'e3_direct_pm', 'sales_manager', 'sales_person', 'project_developer', 'accounting'];
  const defaultsByRole = Object.fromEntries(roleKeys.map((r) => [r, defaultsForRole(r)]));
  const overrideUsers = (users || []).filter((u) => u.permission_overrides && Object.keys(u.permission_overrides).length > 0);
  const colW = 96;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 520 + roleKeys.length * colW }}>
          <colgroup>
            <col style={{ width: 'auto' }} />
            {roleKeys.map((r) => <col key={r} style={{ width: colW }} />)}
          </colgroup>
          <thead>
            <tr>
              <TH>Capability</TH>
              {roleKeys.map((r) => (
                <th key={r} style={{
                  position: 'sticky', top: 0, zIndex: 1, textAlign: 'center', padding: '0 8px', height: 38, whiteSpace: 'nowrap',
                  background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)',
                }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: role(r).text }}>{role(r).short}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAP_GROUPS.map(([group, caps]) => (
              <React.Fragment key={group}>
                <tr>
                  <td colSpan={1 + roleKeys.length} style={{ padding: '8px 16px 4px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{group}</td>
                </tr>
                {caps.map((cap) => (
                  <tr key={cap.key} className="tm-row">
                    <TD>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{cap.label}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-3)', textWrap: 'pretty' }}>{cap.description}</div>
                    </TD>
                    {roleKeys.map((r) => (
                      <TD key={r} align="center"><GrantDot on={!!defaultsByRole[r][cap.key]} /></TD>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>
        These are the <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>role defaults</strong>. To grant or revoke a capability for one person, open <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>Edit → Permissions</strong> on their row.
        {overrideUsers.length > 0 && <> Currently <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>{overrideUsers.length}</strong> {overrideUsers.length === 1 ? 'user has' : 'users have'} a custom override.</>}
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Page
   ═══════════════════════════════════════════════════════════════════════════ */
const UserManagementPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [orgTree, setOrgTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | org | permissions
  const [modal, setModal] = useState(null); // null | { mode, user }
  const [success, setSuccess] = useState('');
  // The list's search box and the top bar's (lib/pageSearch) are one text. Like the list's own
  // box, the bar's shows only on the List view (no placeholder = no box); the text stays put.
  const [query, setQuery] = usePageSearchState(view === 'list' ? 'Search by name, email or role…' : null);
  const [deact, setDeact] = useState(null); // the user awaiting "Deactivate?"
  const [deactBusy, setDeactBusy] = useState(false);
  const [deactError, setDeactError] = useState(null);

  // Freeze the header and tiles: size the page to the window so only the list (inside its
  // card) scrolls. Only on a laptop-or-larger window with room for the list; otherwise the
  // page scrolls as before.
  const pageRef = useRef(null);
  const [frozenH, setFrozenH] = useState(null);
  useLayoutEffect(() => {
    const fit = () => {
      const el = pageRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const h = Math.floor(window.innerHeight - top - 24); // 24 = .app-main's bottom padding
      setFrozenH(window.innerWidth >= 900 && h >= 460 ? h : null);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [loading]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [userData, treeData] = await Promise.all([getUsers(), getOrgTree()]);
      setUsers(Array.isArray(userData) ? userData : []);
      setOrgTree(Array.isArray(treeData) ? treeData : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Enrich org nodes with is_active (from the roster) so deactivated nodes mute.
  const activeById = new Map(users.map((u) => [u.id, !!u.is_active]));
  const orgNodes = orgTree.map((n) => ({ ...n, is_active: n.is_active != null ? n.is_active : (activeById.has(n.id) ? activeById.get(n.id) : true) }));

  const activeCount = (roleKey) => users.filter((u) => u.role === roleKey && u.is_active).length;

  const openNew = () => { setSuccess(''); setModal({ mode: 'create', user: null }); };
  const openEdit = (userOrId) => {
    const u = typeof userOrId === 'object' ? userOrId : users.find((x) => x.id === userOrId);
    if (!u) return;
    setSuccess(''); setModal({ mode: 'edit', user: u });
  };

  // Returns a promise so the modal can show server errors and keep itself open on failure.
  const handleSubmit = async (payload) => {
    if (modal?.mode === 'edit') {
      const data = {
        email: payload.email, first_name: payload.first_name, last_name: payload.last_name,
        role: payload.role, is_active: payload.is_active,
        reports_to: payload.reports_to, region: payload.region,
        permission_overrides: payload.permission_overrides, dashboard: payload.dashboard,
      };
      await updateUser(modal.user.id, data);
      setSuccess(`${payload.first_name} ${payload.last_name} updated.`);
    } else {
      await createUser({
        email: payload.email,
        first_name: payload.first_name, last_name: payload.last_name,
        role: payload.role, reports_to: payload.reports_to, region: payload.region,
        permission_overrides: payload.permission_overrides, dashboard: payload.dashboard,
      });
      setSuccess(`${payload.first_name} ${payload.last_name} added to the team.`);
    }
    setModal(null);
    await fetchData();
  };

  // Deactivate (Facility Navigator's row action): only is_active changes, so their
  // history, projects and reporting line stay. Reactivate from Edit.
  const openDeactivate = (u) => { setSuccess(''); setDeactError(null); setDeact(u); };
  const confirmDeactivate = async () => {
    if (!deact || deactBusy) return;
    setDeactBusy(true);
    setDeactError(null);
    try {
      await updateUser(deact.id, { is_active: false });
      setSuccess(`${fullName(deact)} deactivated.`);
      setDeact(null);
      await fetchData();
    } catch (e) {
      setDeactError(e.message || 'Could not deactivate. Please try again.');
    } finally {
      setDeactBusy(false);
    }
  };

  if (loading && users.length === 0) {
    return <LoadingState message="Loading team…" padding="80px 20px" />;
  }

  const frozen = frozenH != null;
  const shownRoles = ROLE_OPTIONS.filter((r) => activeCount(r.value) > 0);
  const viewBox = frozen ? { flex: 1, minHeight: 0, overflow: 'auto' } : undefined;

  return (
    <div ref={pageRef} style={{ display: 'flex', flexDirection: 'column', gap: 14, ...(frozen ? { height: frozenH } : {}) }}>
      <style>{`
        @keyframes tmSpin { to { transform: rotate(360deg); } }
        .tm-row:hover td { background: var(--bg-hover); }
        .tm-orgnode:hover { border-color: var(--border-strong); }
        .tm-edit:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .tm-primary:hover { background: var(--accent-hover); }
        .tm-modal-x:hover { background: var(--bg-hover); color: var(--text-2); }
        .tm-banner-x:hover { background: color-mix(in oklch, var(--success) 16%, transparent); }
        .tm-ib:not(:disabled):hover { background: var(--bg-hover); border-color: var(--border-default); }
        .tm-ib-danger:not(:disabled):hover { background: var(--danger-soft); border-color: color-mix(in oklch, var(--danger) 30%, transparent); }
      `}</style>

      {/* Region 1 — header + Add user (Facility Navigator's "Team" block) */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Team</h2>
          <p style={{ margin: '2px 0 0', fontSize: 14, color: 'var(--text-3)' }}>
            {users.length} {users.length === 1 ? 'person' : 'people'} · manage roles, reporting lines and permissions · invite-only
          </p>
        </div>
        <button type="button" className="tm-primary" onClick={openNew} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 15px 0 13px', borderRadius: 8,
          fontSize: 14, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', background: 'var(--accent)',
          border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))',
          boxShadow: '0 1px 0 oklch(0.42 0.06 135), 0 1px 2px oklch(0.4 0.05 135 / 0.25)', cursor: 'pointer',
        }}>
          <TmIcon name="plus" size={14} sw={2.2} />Add user
        </button>
      </div>

      {success && <div style={{ flex: 'none' }}><SuccessBanner onDismiss={() => setSuccess('')}>{success}</SuccessBanner></div>}

      {/* Region 2 — one tile per role that has active people */}
      <div style={{ flex: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))', gap: 8 }}>
        {shownRoles.map((r) => <RoleTile key={r.value} roleKey={r.value} count={activeCount(r.value)} />)}
      </div>

      {/* Region 3 — view toggle + search (the list's) */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <SegmentedControl value={view} onChange={setView} options={[
          { value: 'list', label: 'List', icon: <TmIcon name="list" size={14} sw={1.8} /> },
          { value: 'org', label: 'Org chart', icon: <TmIcon name="sitemap" size={14} sw={1.8} /> },
          { value: 'permissions', label: 'Permissions', icon: <TmIcon name="shield" size={14} sw={1.8} /> },
        ]} />
        {view === 'list' && <div style={{ marginLeft: 'auto' }}><SearchBox value={query} onChange={setQuery} /></div>}
      </div>

      {/* Region 4 — view body (the only part that scrolls when frozen) */}
      {view === 'list' && (
        <div style={frozen ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
          <ListView users={users} currentUser={currentUser} query={query} frozen={frozen} onEdit={openEdit} onDeactivate={openDeactivate} onNew={openNew} />
        </div>
      )}
      {view === 'org' && <div style={viewBox}><OrgChart orgTree={orgNodes} onEdit={openEdit} /></div>}
      {view === 'permissions' && <div style={viewBox}><PermissionsMatrix users={users} /></div>}

      {modal && (
        <UserFormModal mode={modal.mode} user={modal.user} orgTree={orgNodes} onClose={() => setModal(null)} onSubmit={handleSubmit} />
      )}

      {deact && (
        <ModalShell title={`Deactivate ${fullName(deact)}?`} width={440} busy={deactBusy}
          onClose={() => { if (!deactBusy) setDeact(null); }}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
              <button type="button" onClick={() => { if (!deactBusy) setDeact(null); }} style={{
                display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 13px', borderRadius: 8,
                fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                boxShadow: 'var(--shadow-xs)', cursor: deactBusy ? 'default' : 'pointer', opacity: deactBusy ? 0.6 : 1,
              }}>Cancel</button>
              <button type="button" onClick={confirmDeactivate} disabled={deactBusy} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 15px', borderRadius: 8,
                fontSize: 14, fontWeight: 600, color: '#fff', background: 'var(--danger)',
                border: '1px solid color-mix(in oklch, var(--danger) 55%, oklch(0.3 0.05 25))', cursor: deactBusy ? 'default' : 'pointer', opacity: deactBusy ? 0.85 : 1,
              }}>
                {deactBusy ? <TmIcon name="spin" size={13} sw={2.4} /> : <TmIcon name="user-off" size={13} sw={1.9} />}
                {deactBusy ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          }>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {deactError && <ErrorBanner>{deactError}</ErrorBanner>}
            <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
              They’ll keep their history but can’t sign in. You can reactivate them from Edit.
            </p>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default UserManagementPage;
